/**
 * How long one oRPC round trip may take, per procedure.
 *
 * Every call is bounded at the transport (`fetch-orpc-with-deadline.ts`): React Native's `fetch` has
 * no timeout at all, so before this a backend that accepted the connection and never answered left
 * the awaiting button or screen spinning until the app was killed.
 */

/**
 * The default budget for one round trip, headers AND body.
 *
 * 30s matches the per-attempt ceiling `use-actor-sync` already put on `database.actor.ensure` after
 * the staging worker's wedged migration memo held every ensure open until the CDN killed it at
 * ~100s: long enough for a cold Worker plus a Turso/D1 round trip on cellular, short enough that a
 * hung request becomes a retryable "timed out" error while the user is still looking.
 */
export const ORPC_DEFAULT_DEADLINE_MS = 30_000;

/** Procedures that commit through the GitHub API make several sequential GitHub calls. */
const GITHUB_BACKED_PROCEDURE_DEADLINE_MS = 60_000;

/**
 * Procedures whose server work legitimately outlasts the default. Keyed by the dotted procedure
 * path (`cards.mergePr`).
 *
 * These are also EXCLUDED FROM BATCHING (`orpc-link-plugins.ts`): a batch shares one HTTP request,
 * so its deadline is the default one and a slow member would either be cut off early or hold every
 * fast sibling for its whole budget. Unbatched, each slow call owns its request and its deadline.
 */
const SLOW_PROCEDURE_DEADLINES_MS: Readonly<Record<string, number>> = {
  "cards.closePr": GITHUB_BACKED_PROCEDURE_DEADLINE_MS,
  "cards.commitBack": GITHUB_BACKED_PROCEDURE_DEADLINE_MS,
  "cards.commitBroken": GITHUB_BACKED_PROCEDURE_DEADLINE_MS,
  "cards.commitCompleted": GITHUB_BACKED_PROCEDURE_DEADLINE_MS,
  "cards.commitContent": GITHUB_BACKED_PROCEDURE_DEADLINE_MS,
  "cards.commitPhrase": GITHUB_BACKED_PROCEDURE_DEADLINE_MS,
  "cards.commitPrompt": GITHUB_BACKED_PROCEDURE_DEADLINE_MS,
  "cards.createMnemonicsPr": 125_000,
  "cards.mergePr": GITHUB_BACKED_PROCEDURE_DEADLINE_MS,
  "cards.replaceContent": GITHUB_BACKED_PROCEDURE_DEADLINE_MS,
  // Aggregates the whole live card corpus (its own test allows 60s).
  "cards.stats": GITHUB_BACKED_PROCEDURE_DEADLINE_MS,
  "cards.updatePrFile": GITHUB_BACKED_PROCEDURE_DEADLINE_MS,
};

function toProcedureKey(path: readonly string[]): string {
  return path.join(".");
}

export function getOrpcProcedureDeadlineMs(path: readonly string[]): number {
  return SLOW_PROCEDURE_DEADLINES_MS[toProcedureKey(path)] ?? ORPC_DEFAULT_DEADLINE_MS;
}

/** Whether a procedure's budget exceeds the default, so it must travel in its own request. */
export function isSlowOrpcProcedure(path: readonly string[]): boolean {
  return toProcedureKey(path) in SLOW_PROCEDURE_DEADLINES_MS;
}
